/**
 * RBAC (Role-Based Access Control) Type Definitions
 * 
 * Defines roles, permissions, and related types for the Admin Ops UI.
 * Based on the Admin Ops UI Specification.
 */

/**
 * User roles in the system
 * 
 * NOTE: These roles are ONLY for Admin Ops UI access.
 * Regular app users (learners) have no role (null) and cannot access admin features.
 * 
 * - Admin: Full control over all operations
 * - Editor: Content creation and editing, cannot approve or publish
 * - Reviewer: Review and approve content, cannot publish
 * - null/undefined: Regular app user with no admin access
 */
export enum Role {
  Admin = 'admin',
  Editor = 'editor',
  Reviewer = 'reviewer'
}

/**
 * System permissions aligned with Admin Ops UI workflows
 * 
 * These permissions map to specific actions in the clip lifecycle
 * and admin UI pages/buttons as defined in the specification.
 */
export enum Permission {
  // Clip Creation & Editing
  CREATE_CLIP = 'create_clip',
  EDIT_CLIP = 'edit_clip',
  EDIT_PUBLISHED_CLIP = 'edit_published_clip', // Create change request
  
  // Content Review & Approval
  SUBMIT_FOR_REVIEW = 'submit_for_review',
  APPROVE_TEXT = 'approve_text',
  APPROVE_AUDIO = 'approve_audio',
  REJECT_CONTENT = 'reject_content',
  ADD_REVIEW_COMMENTS = 'add_review_comments',
  EDIT_PRE_PUBLISH = 'edit_pre_publish', // Direct edits during review
  
  // Publishing & Versioning
  PUBLISH_CLIP = 'publish_clip',
  ROLLBACK_VERSION = 'rollback_version',
  
  // Automated Operations
  TRIGGER_VALIDATION = 'trigger_validation',
  TRIGGER_REGENERATION = 'trigger_regeneration',
  TRIGGER_AUDIO_GENERATION = 'trigger_audio_generation',
  
  // Prompt & Model Management
  VIEW_PROMPTS = 'view_prompts',
  CREATE_PROMPT = 'create_prompt',
  EDIT_PROMPT = 'edit_prompt',
  APPROVE_PROMPT = 'approve_prompt',
  
  VIEW_MODELS = 'view_models',
  MANAGE_MODELS = 'manage_models',
  
  // Voice Management
  VIEW_VOICES = 'view_voices',
  MANAGE_VOICES = 'manage_voices',
  SELECT_VOICE = 'select_voice',
  
  // User Management
  VIEW_USERS = 'view_users',
  INVITE_USER = 'invite_user',
  MANAGE_USER_ROLES = 'manage_user_roles',
  
  // Jobs & Cost Monitoring
  VIEW_JOBS = 'view_jobs',
  VIEW_COST_DASHBOARD = 'view_cost_dashboard',
  
  // Settings & Configuration
  MANAGE_SETTINGS = 'manage_settings'
}

/**
 * Role-to-Permissions mapping
 * 
 * Defines the complete permission matrix for each role.
 * Based on the Admin Ops UI Spec sections on "Roles & Access (RBAC)"
 * and "Lifecycle & State Machine".
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [Role.Admin]: [
    // Full control - all permissions
    Permission.CREATE_CLIP,
    Permission.EDIT_CLIP,
    Permission.EDIT_PUBLISHED_CLIP,
    Permission.SUBMIT_FOR_REVIEW,
    Permission.APPROVE_TEXT,
    Permission.APPROVE_AUDIO,
    Permission.REJECT_CONTENT,
    Permission.ADD_REVIEW_COMMENTS,
    Permission.EDIT_PRE_PUBLISH,
    Permission.PUBLISH_CLIP,
    Permission.ROLLBACK_VERSION,
    Permission.TRIGGER_VALIDATION,
    Permission.TRIGGER_REGENERATION,
    Permission.TRIGGER_AUDIO_GENERATION,
    Permission.VIEW_PROMPTS,
    Permission.CREATE_PROMPT,
    Permission.EDIT_PROMPT,
    Permission.APPROVE_PROMPT,
    Permission.VIEW_MODELS,
    Permission.MANAGE_MODELS,
    Permission.VIEW_VOICES,
    Permission.MANAGE_VOICES,
    Permission.SELECT_VOICE,
    Permission.VIEW_USERS,
    Permission.INVITE_USER,
    Permission.MANAGE_USER_ROLES,
    Permission.VIEW_JOBS,
    Permission.VIEW_COST_DASHBOARD,
    Permission.MANAGE_SETTINGS
  ],
  
  [Role.Editor]: [
    // Content creation and editing
    Permission.CREATE_CLIP,
    Permission.EDIT_CLIP,
    Permission.EDIT_PUBLISHED_CLIP,
    Permission.SUBMIT_FOR_REVIEW,
    
    // Automated operations
    Permission.TRIGGER_VALIDATION,
    Permission.TRIGGER_REGENERATION,
    Permission.TRIGGER_AUDIO_GENERATION,
    
    // Prompt/Model selection (from allowlist)
    Permission.VIEW_PROMPTS,
    Permission.VIEW_MODELS,
    
    // Voice selection
    Permission.VIEW_VOICES,
    Permission.SELECT_VOICE,
    
    // Jobs visibility
    Permission.VIEW_JOBS,
    Permission.VIEW_COST_DASHBOARD
  ],
  
  [Role.Reviewer]: [
    // Review and approval
    Permission.APPROVE_TEXT,
    Permission.APPROVE_AUDIO,
    Permission.REJECT_CONTENT,
    Permission.ADD_REVIEW_COMMENTS,
    Permission.EDIT_PRE_PUBLISH,
    
    // View content
    Permission.VIEW_PROMPTS,
    Permission.VIEW_MODELS,
    Permission.VIEW_VOICES,
    
    // Propose prompt changes (can view/edit, admin approves)
    Permission.CREATE_PROMPT,
    Permission.EDIT_PROMPT,
    
    // Jobs visibility
    Permission.VIEW_JOBS,
    Permission.VIEW_COST_DASHBOARD
  ]
};

/**
 * User information with role
 */
export interface User {
  /** User ID from authentication system */
  id: string;
  
  /** User email */
  email?: string;
  
  /** 
   * Assigned role for Admin Ops UI access
   * null/undefined = regular app user with no admin permissions
   */
  role?: Role | null;
  
  /** Display name (optional) */
  name?: string;
  
  /** When user was created */
  createdAt?: string;
  
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Permission check result with optional reason
 */
export interface PermissionCheckResult {
  /** Whether permission is granted */
  allowed: boolean;
  
  /** Optional reason if denied */
  reason?: string;
}

/**
 * Clip state from the Admin Ops workflow
 */
export enum ClipState {
  Draft = 'Draft',
  TextReview = 'TextReview',
  TextApproved = 'TextApproved',
  AudioQueued = 'AudioQueued',
  AudioReady = 'AudioReady',
  AudioReview = 'AudioReview',
  AudioApproved = 'AudioApproved',
  Published = 'Published'
}

/**
 * Change type for published content updates
 */
export enum ChangeType {
  MinorFix = 'MinorFix',
  TextRevision = 'TextRevision',
  AudioOnlyUpdate = 'AudioOnlyUpdate'
}

/**
 * Context for permission checks that may require additional validation
 */
export interface PermissionContext {
  /** Clip ID being acted upon */
  clipId?: string;
  
  /** Current clip state */
  clipState?: ClipState;
  
  /** User who created/drafted the clip */
  createdBy?: string;
  
  /** Change type for versioned updates */
  changeType?: ChangeType;
  
  /** Any other contextual information */
  [key: string]: unknown;
}
